import { TrackJS } from 'trackjs';

import Logger from '@module/logger';

import Config from './config';
import Job from './job';

export default class Queue {
  constructor(transfer) {
    this.transfer = transfer;
    this.aborted = false;
    this.resolve = this.reject = () => false;
    this.jobs = [];

    // We start with the low end of our limit, and
    // scale it up to `Config.queue.parallel.max`
    // if allowed by the connection.
    this.jobLimit = Config.queue.parallel.min;
  }

  abort(silent = false) {
    this.pause();

    this.aborted = true;
    this.reject(silent ? 'TransferAbortedSilent' : 'TransferAborted');
    this.resolve = this.reject = () => false;
  }

  pause() {
    for (let n = this.jobs.length; n--; ) {
      this.jobs[n].abort();
      this.jobs.splice(n, 1);
    }
  }

  resume() {
    // Create our runners
    this.createJobs();
  }

  start() {
    // Check if the queue is dirty (pre-used)
    if (this.jobs.length) {
      throw 'QueueIsDirty';
    }

    // Return a promise that we can resolve or reject
    // as needed in the future.
    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;

      // Create jobs (for all the Americans)
      this.createJobs();
    });
  }

  get availableJobs() {
    // If we have more jobs than allowed, we don't want new ones
    if (this.jobs.length >= this.jobLimit) {
      return 0;
    }

    // Calculate actual available slots
    const availableSlots = this.jobLimit - this.jobs.length;
    if (availableSlots <= 0) {
      return 0;
    }

    if (availableSlots <= 0) {
      return 0;
    }
    return availableSlots;
  }

  createJobs() {
    const availableJobs = this.availableJobs;

    // Create our runners
    for (let n = availableJobs; n--; ) {
      this.startJob();
    }
  }

  async startJob() {
    // Don't start jobs if transfer hasn't started
    if (this.transfer.state !== 'started') {
      return;
    }

    // Don't start more jobs than allowed
    if (this.jobs.length >= this.jobLimit) {
      return;
    }

    // Get the first file with remaining chunks
    const file = this.transfer.files.find((file) => file.hasRemainingChunks());
    if (!file) {
      return this.jobs.length === 0 && this.resolve();
    }

    // Get the first remaining chunk
    const chunk = file.nextChunk();
    if (chunk === false) {
      return;
    }

    // Prepare a new job for this transfer, file and chunk(number)
    let job = new Job(this.transfer, file, chunk);

    // Add it to the list.
    this.jobs.push(job);

    // Actually start the job and it's a promise so whatevs.
    try {
      const gauge = await job.start(this);

      // Scale up, if allowed and upload was fast enough
      if (
        gauge.uploadSpeed >= Config.queue.parallel.minSpeed &&
        this.jobLimit < Config.queue.parallel.max
      ) {
        this.jobLimit++;
      }

      // Is this file finished now?
      if (!this.fileInQueue(job.file, job) && !job.file.hasRemainingChunks()) {
        await job.finalizeFile(job.file);
      }

      // Finish up the job
      this.finishJob(job);

      // invalidate job
      job = null;
    } catch (error) {
      if (error === 'JobStopped' || this.aborted) {
        return; // The transfer was aborted or paused
      }

      TrackJS.addMetadata('transfer_id', this.transfer.id);
      TrackJS.track(error);

      Logger.error('Job Error', error);

      this.reject('TransferFailed');
      this.pause(); // stops everything
      this.resolve = this.reject = () => false;
    }
  }

  removeFromQueue(job) {
    // Remove from current jobs.
    for (let n = this.jobs.length; n--; ) {
      if (this.jobs[n] !== job) {
        continue;
      }
      this.jobs.splice(n, 1);
    }
  }

  fileInQueue(file, job) {
    // Check if the file is already in the queue
    for (let n = this.jobs.length; n--; ) {
      if (this.jobs[n].file === file && this.jobs[n] !== job) {
        return true;
      }
    }

    return false;
  }

  finishJob(job) {
    this.removeFromQueue(job);
    this.createJobs();
  }
}
